import React from 'react';
import { withRouter } from 'react-router-dom';
import { observable, action, computed, toJS, when, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Dropdown, Checkbox, DefaultButton, Link, MessageBar, MessageBarType, MessageBarButton } from '@fluentui/react';
import { ResponsiveMode, mergeStyleSets, FontSizes, FontWeights } from '@fluentui/react';

import { runShortAction } from 'src/services/utils';
import GroupPane from 'src/components/base/GroupPane';
import SearchableComboBox from 'src/components/base/SearchableComboBox';
import NumericField from 'src/components/base/NumericField';
import TimeField from 'src/components/base/TimeField';
import WeekdaySelector from 'src/components/base/WeekdaySelector';
import ConfirmDialog, { CloseDialog } from 'src/components/base/ConfirmDialog';
import SlackChannelBrowser from './SlackChannelBrowser';
import MonthdaySelector from 'src/components/base/MounthdaySelector';
import CellAddressPicker from '@v2/components/base/CellAddressPicker';

import { scheduleToUtc, scheduleToLocal } from 'shared/services/scheduler';
import { Type } from 'shared/utils/flow';
import { CURRENT_VERSION } from 'shared/migration/action';


import office from 'src/services/office';

const SCHEDULE_TYPE_HOURLY = 'hourly';
const SCHEDULE_TYPE_DAILY = 'daily';
const SCHEDULE_TYPE_WEEKLY = 'weekly';
const SCHEDULE_TYPE_MANUALLY = 'manually';
const SCHEDULE_TYPE_MONTHLY = 'monthly';

const SCHEDULE_NOTIFY_DEFAULT = {
  onError: true, onSuccess: false,
  slack: { 
    channel: '',
    onError: true,
    onSuccess: false,
  },
  enableEventsLog: true,
};

const SCHEDULE_MONTHLY_DEFAULT = {
  position: 'first',
  type: 'weekday',
  weekday: 'Sun',
};

const classNames = mergeStyleSets({
  actionPage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    paddingBottom: 8,
    '& > *': {
      margin: '16px 16px 0px 16px',
    },
  },
  buttonBlock: {
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'stretch',
    padding: '0px 8px 0px 8px',
    marginTop: 26,
  
    '& > *': {
      flexGrow: 1,
      margin: '0px 8px 0px 8px',
    },
  },
  subtitle: {
    margin: '8px 16px 0px 16px',
    fontSize: FontSizes.medium,
    fontWeight: FontWeights.semibold,
  },
  tsPicker: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  monthOptionsContainer: {
    width: '70%'
  },
})

@withRouter
@inject('officeStore', 'dataStore', 'userStore')
@observer
export default class Action extends React.Component {
  @observable options = {
    type: SCHEDULE_TYPE_HOURLY,
    salesForceObject: null,
    recordIdCell: null,
    minute: 0,
    time: '04:00',
    weekday: ['Sun'],
    month: SCHEDULE_MONTHLY_DEFAULT,
    notify: SCHEDULE_NOTIFY_DEFAULT,
  }

  @observable action = null;
  @observable category = Type.Pull;
  @observable flowId = '';
  @observable name = '';
  @observable isActive = true;
  @observable worksheets = null;
  @observable availableWorksheets = [];
  @observable showDeleteConfirmation = false;
  @observable showManpackOverrideConfirmation = false;
  @observable showFlowsDropdown = true;
  @observable manuallyScheduleAvailable = null;
  @observable showObjectsLabels = false;
  @observable lastUpdateWorksheetName = '';
  @observable preparedSaveData = null;
  @observable manpackRegisteredFlow = {};
  @observable isRecordIdCellValid = true;
 

  @computed get isReadonly() {
    return (!!this.action && this.action.owner_name !== this.props.userStore.userName) ||
      (this.options.type === SCHEDULE_TYPE_WEEKLY && this.options.weekday.length < 1) ||
      (this.worksheets && this.worksheets.length < 1) ||
      (this.options.type === SCHEDULE_TYPE_MANUALLY && (!this.manuallyScheduleAvailable || !this.options.salesForceObject)) ||
      (!this.isRecordIdCellValid);
  }

  @computed get flowListOptions() {
    return this.props.dataStore.flows.map(({ name: text, id, type }) => ({
      key: id, text, id, type, 
    }));
  }

  constructor(props) {
    super(props);
    makeObservable(this);

    const { params } = props.match;
    this.action = props.dataStore.actions.find(item => item.id === +params.id);
    if (this.action) {
      const scheduleLocal = scheduleToLocal(this.action.schedule);
      this.category = this.action.action;
      this.flowId = this.action.flow_id;
      this.isActive = this.action.is_active;
      this.worksheets = this.action.worksheets;
      this.options = {
        type: scheduleLocal.type,
        salesForceObject: this.action.schedule.salesForceObject,
        recordIdCell: this.action.schedule.recordIdCell,
        minute: scheduleLocal.minute || 0,
        time: scheduleLocal.time || '04:00',
        weekday: scheduleLocal.weekday || ['Sun'],
        month: scheduleLocal.month || SCHEDULE_MONTHLY_DEFAULT,
        notify: { ...SCHEDULE_NOTIFY_DEFAULT, ...this.action.schedule.notify },
      }
    }
    else {
      this.flowId = props.dataStore.flows[0].id;
    }

    if (params.flowId) {
      this.flowId = params.flowId;
      this.showFlowsDropdown = false;
    }

    when(
      () => this.options.type === SCHEDULE_TYPE_MANUALLY,
      () => this.updateMannpackInstallation()
    );
    when(
      () => this.options.type === SCHEDULE_TYPE_MANUALLY && this.manuallyScheduleAvailable,
      () => {
        this.props.officeStore.fetchObjects();
        this.props.officeStore.contextStore.updateNamedRanges();
      }
    );   
  }

  async componentDidMount() {
    await this.refreshSheets();
  }

  @action async refreshSheets() {
    const worksheets = await office.getAllWorksheets();
    this.availableWorksheets = worksheets.map(i => ( { key: i.id, text: i.name }));
  }

  @computed get objects() {
    const selector = this.showObjectsLabels ? i => i.label : i => i.name;
    return (this.props ? this.props.officeStore.objects : []).map(i => ({ key: i.name, text: selector(i) }));
  }

  render() {
    if (this.options.type === SCHEDULE_TYPE_MANUALLY && !(this.manuallyScheduleAvailable ?? true)) {
      return <ManpackMessage onRefreshClick={this.updateMannpackInstallation} />
    }
    
    const slack = this.props.userStore.isSlackAuthorised;

    return (
      <div className={classNames.actionPage}>

        { this.showFlowsDropdown &&
        <>
          <div className={classNames.block}>
            <Dropdown
              selectedKey={this.flowId}
              label={'Flow name'}
              responsiveMode={ResponsiveMode.large}
              onChange={(_, item) => this.handleFlowChange(item) }
              options={this.flowListOptions}
            />
            <Checkbox label='Active'
              checked={this.isActive}
              onChange={(_, checked) => this.isActive = checked}
            />
          </div>

          { this.action && this.action.worksheets && 
          <div className={classNames.block}>
            
            <Checkbox label='All Worksheets'
              checked={this.worksheets == null}
              onChange={(_, checked) => this.worksheets = checked ? null : [] }
              />
            { this.worksheets &&
              <Dropdown
              multiSelect
              selectedKeys={toJS(this.worksheets)}
              onChange={this.handleWorksheetChange}
              responsiveMode={ResponsiveMode.large}
              options={this.availableWorksheets}
              />
            }
          </div>
          }
        </>
        }
        <div className={classNames.block}>
          <Dropdown
            label='Schedule'
            selectedKey={this.options.type}
            onChange={(_, item) => this.handleModelChange(item.key, 'type')}
            responsiveMode={ResponsiveMode.large}
            ref={this.typePickerRef}
            options={[           
              { key: SCHEDULE_TYPE_HOURLY, text: 'Hourly' },
              { key: SCHEDULE_TYPE_DAILY, text: 'Daily' },
              { key: SCHEDULE_TYPE_WEEKLY, text: 'Weekly' },
              { key: SCHEDULE_TYPE_MONTHLY, text: 'Monthly' },
              { key: SCHEDULE_TYPE_MANUALLY, text: 'Run manually from Salesforce' },
            ]}
          />

          {this.options.type === SCHEDULE_TYPE_MANUALLY &&
            <>
              <Checkbox label='Show Labels'
                checked={this.showObjectsLabels}
                onChange={(_, checked) => this.showObjectsLabels = checked}
                disabled={!this.objects.length || !this.manuallyScheduleAvailable}
              />
              <div>
                <SearchableComboBox
                  label="SF object"
                  placeholder="Select object"
                  selectedKey={this.options.salesForceObject}
                  onChange={async (event, item) => this.handleModelChange(item.key, 'salesForceObject')}
                  options={this.objects}
                  disabled={!this.objects.length || !this.manuallyScheduleAvailable}
                />
              </div>
            </>

          }

          {this.options.type === SCHEDULE_TYPE_HOURLY &&
            <div>
              <NumericField min={0} max={59} pad={2}
                label='Minute' labelPosition={-1} // Ugly margin. Wait for some MS fixes
                value={this.options.minute}
                onChange={value => this.handleModelChange(value, 'minute')}
              />
            </div>
          }

          { this.options.type === SCHEDULE_TYPE_DAILY &&
            <TimeField value={this.options.time} onChange={value => this.handleModelChange(value, 'time')} />
          }

          { this.options.type === SCHEDULE_TYPE_WEEKLY &&
            <React.Fragment>
              <TimeField value={this.options.time} onChange={value => this.handleModelChange(value, 'time')} />
              <WeekdaySelector value={this.options.weekday} onChange={value => this.handleModelChange(value, 'weekday')} />
            </React.Fragment>
          }

          {this.options.type === SCHEDULE_TYPE_MONTHLY &&
            <React.Fragment>
              <TimeField value={this.options.time} onChange={value => this.handleModelChange(value, 'time')} />
              <div className={classNames.monthOptionsContainer} >
                <MonthdaySelector value={this.options.month} onChange={value => this.handleModelChange(value, 'month')} />
              </div>
            </React.Fragment>
          }
        </div>

        <GroupPane title='Notifications' collapsed={true}>
          <div>
            <div className={classNames.subtitle}>Email Notifications</div>

            <div className={classNames.block}>
              <Checkbox label="Notify on failure"
                checked={this.options.notify.onError}
                onChange={(_, checked) => this.options.notify.onError = checked}
              />
            </div>

            <div className={classNames.block}>
              <Checkbox label="Notify on success"
                checked={this.options.notify.onSuccess}
                onChange={(_, checked) => this.options.notify.onSuccess = checked}
              />
            </div>
          </div>

          <div>
            <div className={classNames.subtitle}>Slack Notifications</div>

            <div className={classNames.block}>
              <SlackChannelBrowser
                selectedId={this.options.notify.slack?.channel}
                onChange={this.handleSlackChannelChange} 
              />
            </div>

            { slack &&
              <>
                <div className={classNames.block}>
                  <Checkbox label="Notify on failure"
                    checked={this.options.notify.slack.onError}
                    onChange={(_, checked) => this.options.notify.slack.onError = checked}
                  />
                </div>

                <div className={classNames.block}>
                  <Checkbox label="Notify on success"
                    checked={this.options.notify.slack.onSuccess}
                    onChange={(_, checked) => this.options.notify.slack.onSuccess = checked}
                  />
                </div>
              </>
            }
          </div>

        </GroupPane>        

        <GroupPane title='Options' collapsed={true}>
          <div>

            <div className={classNames.block}>
              <Checkbox label="Enable Events log"
                checked={this.options.notify.enableEventsLog}
                onChange={(_, checked) => this.options.notify.enableEventsLog = checked}
              />
            </div>
            
            {this.options.type === SCHEDULE_TYPE_MANUALLY &&
              <div className={classNames.block}>
                <CellAddressPicker
                  label={"Record id:"}
                  value={this.options.recordIdCell}
                  onChange={(value, isValid) => this.handleRecordIdCellChange(value, isValid)}
                  allowEmptyCell={true}
                  localAddressSheetIdGetter={() => this.props.officeStore.activeWorksheetId}
                />
              </div>
            }
          </div>
        </GroupPane>

        <div className={classNames.buttonBlock}>
          {!!this.action &&
            <DefaultButton text="Delete" onClick={() => this.showDeleteConfirmation = true} disabled={this.isReadonly} />
          }
          <DefaultButton primary={true} text="Save" onClick={this.handleSaveClick} disabled={this.isReadonly} />
        </div>

        <ConfirmDialog
          title={'Delete the action?'}
          text={'Are you sure you want to permanently remove this action?'}
          hidden={!this.showDeleteConfirmation}
          onConfirm={this.handleConfirmDeleteAction}
          onDismiss={() => this.showDeleteConfirmation = false}
        />

        <CloseDialog
          title={'Override registered object?'}
          text={`This object has already been registered for flow ${this.manpackRegisteredFlow.flowName}.`}
          content={this.manpackRegisteredFlow.documentLink &&
          <Link href={this.manpackRegisteredFlow.documentLink}>
            Flow's document.
          </Link>}
          hidden={!this.showManpackOverrideConfirmation}
          onDismiss={() => this.showManpackOverrideConfirmation = false}
          onForget={() => this.showManpackOverrideConfirmation = false}
          onSave={async () => {
            await this.saveFlow(this.preparedSaveData);
            this.props.goBack();
          }}
        />

      </div>
    )
  }

  handleFlowChange = (flow) => {
    this.flowId = flow.id;
    this.category = flow.type;
  }

  handleWorksheetChange = (event, item) => {
    this.worksheets = item.selected ? [...this.worksheets, item.key] : this.worksheets.filter(i => i !== item.key);
  }


  handleShowLabelsChange = (event, value) => {
    this.showLabels = value;
  }

  handleModelChange = (value, index) => {
    this.options[index] = value;
  }

  @action
  handleRecordIdCellChange = (cell, valid) => {
    this.handleModelChange(cell, 'recordIdCell');
    this.isRecordIdCellValid = valid;
  }

  saveFlow = async (data) => {
    if (!!this.action) {
      await this.props.dataStore.updateAction({ id: this.action.id, ...data });
    }
    else {
      await this.props.dataStore.createAction(data);
    }
  }

  handleSaveClick = async () => {
    await runShortAction(async () => {
      const scheduleUtc = scheduleToUtc(this.options);
      const data = {
        version: CURRENT_VERSION,
        action: this.category,
        flow_id: this.flowId,
        worksheets: this.worksheets,
        is_active: this.isActive,
        schedule: Object.assign({
          type: scheduleUtc.type,
          notify: this.options.notify,
        },
          scheduleUtc.type === SCHEDULE_TYPE_MANUALLY && {
            salesForceObject: this.options.salesForceObject,
            recordIdCell: this.options.recordIdCell
          },
          scheduleUtc.type === SCHEDULE_TYPE_HOURLY && { minute: scheduleUtc.minute },
          scheduleUtc.type === SCHEDULE_TYPE_DAILY && { time: scheduleUtc.time },
          scheduleUtc.type === SCHEDULE_TYPE_WEEKLY && { time: scheduleUtc.time, weekday: scheduleUtc.weekday },
          scheduleUtc.type === SCHEDULE_TYPE_MONTHLY && {time: scheduleUtc.time, month: scheduleUtc.month },
        ),
      };

      // Flowsteps are read by sheduler from document
      // data.flowsteps = this.props.dataStore.flowSteps.filter(x => x.category === this.category);
      // if (this.worksheets) {
      //   data.flowsteps = data.flowsteps.filter(i => this.worksheets.some(id => id === i.worksheetId));
      // }

      let manpackRegistered;


      if (data.schedule.type === SCHEDULE_TYPE_MANUALLY &&
        this.action?.schedule.salesForceObject !== data.schedule.salesForceObject) {
        manpackRegistered = await this.props.dataStore.getManpackRegisteredFlow(data.schedule.salesForceObject);
      }

      if (manpackRegistered?.flowName) {
        this.preparedSaveData = data;
        this.showManpackOverrideConfirmation = true;
        this.manpackRegisteredFlow = manpackRegistered;
      }
      else {
        await this.saveFlow(data);
        this.props.goBack();
      }
    });
  }

  handleConfirmDeleteAction = async () => {
    this.showDeleteConfirmation = false;

    runShortAction(async () => {
      await this.props.dataStore.deleteAction(this.action);
      this.props.goBack();
    });
  }

  handleSlackChannelChange = (channel) => {
    this.options.notify.slack.channel = channel;
  }

  @action
  updateMannpackInstallation = async () => {
    await runShortAction(async () => { 
      this.manuallyScheduleAvailable = await this.props.dataStore.isManpackInstalled();
    });  
  }

}

const ManpackMessage = ({ onRefreshClick }) => (
  <MessageBar
    messageBarType={MessageBarType.warning}
    isMultiline={true}
    actions={
      <div className={classNames.warningButton} >
        <MessageBarButton onClick={onRefreshClick}>Refresh</MessageBarButton>
      </div>
    }>
    To use manual triggering of XL-Connector Flows from a Salesforce record page, you will need to install the
    <Link
      href="https://appexchange.salesforce.com/appxListingDetail?listingId=a0N3A00000FP1PrUAL" target={'_blank'}>
      XL-Connector
    </Link>
    {" "}managed package in your Salesforce instance.
  </MessageBar>
)